import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import Loading from 'core-system/Loading'
import styled from 'styled-components'
import { StaticAssetsUrl } from 'redux/config/services/Constants'
import Text from 'core-system/Text'
import { employerOnboardingStepData } from '../OnboardingUtils'
import OnboardingWorkforceSetup from './OnboardingWorkforceSetup'
import OnboardingUploadCsv from './OnboardingUploadCsv'
import OnboardingUploadCsvSteps from './OnboardingUploadCsvSteps'
import OnboardingFinchSync from './OnboardingFinchSync'
import { useDispatch } from 'react-redux'
import PageWrapper from '../components/PageWrapper'
import { onboardingActions } from '../onboardingActions'
import RightImage from '../components/RightImage'

const Uploading = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const UploadImg = styled.img`
  width: ${(props) => props.theme.pxToRem(460)};
  padding-top: 1.25rem;
`

const ProgressBar = styled.div`
  height: 0.75rem;
  width: 100%;
  border-radius: 0.5rem;
  margin-top: 5rem;
  background-color: ${(props) => props.theme.palette.secondary.purple600};
`

const Progress = styled.div<{ percentage: number }>`
  width: ${(props) => `${props.percentage}%`};
  height: 100%;
  border-radius: 0.5rem;

  background-color: ${(props) => props.theme.palette.secondary.purple600};
  transition: width 0.3s;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
`

export type WorkforceSyncStage =
  | 'setupWorkforce'
  | 'finchSync'
  | 'uploading'
  | 'csvUpload'

const WorkforceSyncStage = React.memo(() => {
  const dispatch = useDispatch()
  const onboarding = useSelector((state: AppState) => state.onboarding)

  const [workforceSyncStage, setWorkforceSyncStage] =
    useState<WorkforceSyncStage>('setupWorkforce')

  const [fakeProcessing, setFakeProcessing] = useState(0)
  const [loading, setLoading] = useState(false)

  const setOnboardingStageToStripeSync = () => {
    dispatch(onboardingActions.updateOnboardingStage('STRIPE_SYNC'))
  }

  const getContent = (
    workforceSyncStage: WorkforceSyncStage,
    timer: number
  ) => {
    switch (workforceSyncStage) {
      case 'setupWorkforce':
        return (
          <OnboardingWorkforceSetup
            setLoading={setLoading}
            setWorkforceSyncStage={setWorkforceSyncStage}
          />
        )
      case 'finchSync':
        return (
          <OnboardingFinchSync
            setLoading={setLoading}
            setOnboardingStageToStripeSync={setOnboardingStageToStripeSync}
          />
        )
      case 'csvUpload':
        return (
          <OnboardingUploadCsvSteps
            setLoading={setLoading}
            setOnboardingStageToStripeSync={setOnboardingStageToStripeSync}
          />
        )
      case 'uploading':
        return (
          <Uploading>
            <UploadImg
              alt='upload'
              src={`${StaticAssetsUrl}/onboarding/onboarding-processing.png`}
            />
            <ProgressBar>
              <Progress percentage={timer > 65 ? 65 : timer} />
            </ProgressBar>
          </Uploading>
        )
      default:
        return (
          <Text variant='body1'>
            Oops, something went wrong, try again or contact support
          </Text>
        )
    }
  }

  return (
    <PageWrapper
      title={employerOnboardingStepData[workforceSyncStage].title}
      showBottomContainer={workforceSyncStage !== 'uploading'}
      onboardingStage={onboarding.stage}
      leftContent={
        <>
          {employerOnboardingStepData[workforceSyncStage].subtitle &&
          !loading ? (
            <Text
              variant='h3'
              color='black'
              marginBottom='2rem'
              fontFamily={'PolySans'}
              fontWeight={500}
            >
              {employerOnboardingStepData[workforceSyncStage].subtitle}
            </Text>
          ) : null}
          {loading ? (
            <LoadingContainer>
              <Loading isCard={false} />
              <Text variant='h3' textAlign='center'>
                {employerOnboardingStepData[workforceSyncStage].loading}
              </Text>
            </LoadingContainer>
          ) : (
            getContent(workforceSyncStage, fakeProcessing)
          )}
        </>
      }
      rightContent={
        <>
          {workforceSyncStage !== 'csvUpload' ? (
            <>
              <RightImage imageType={workforceSyncStage} />
            </>
          ) : (
            <OnboardingUploadCsv
              setOnboardingStageToStripeSync={setOnboardingStageToStripeSync}
            />
          )}
        </>
      }
    />
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  WorkforceSyncStage.displayName = 'WorkforceSyncStage'
}

export default WorkforceSyncStage
